import { useContext, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  Checkbox,
  Chip,
  alpha,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';
import { useVariantMatrix } from '../../../hooks/useVariants';
import type { Product, VariantMatrixCell } from '../../../types';

interface VariantMatrixViewProps {
  product: Product;
  onVariantToggle?: (rowValue: string, colValue: string, enabled: boolean) => void;
  onCellClick?: (rowValue: string, colValue: string, cell: VariantMatrixCell) => void;
}

export const VariantMatrixView = ({
  product,
  onVariantToggle,
  onCellClick
}: VariantMatrixViewProps) => {
  const { mode } = useContext(ColorModeContext);
  const [rowAttribute, setRowAttribute] = useState('size');
  const [columnAttribute, setColumnAttribute] = useState('color');

  const { data: matrix, isLoading, error } = useVariantMatrix(product.id, {
    rows: rowAttribute,
    cols: columnAttribute,
  });

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const handleCellToggle = (rowValue: string, colValue: string, cell: VariantMatrixCell) => {
    if (onVariantToggle) {
      onVariantToggle(rowValue, colValue, !cell.exists);
    }
  };

  const handleCellClickInternal = (rowValue: string, colValue: string, cell: VariantMatrixCell) => {
    if (onCellClick && cell.exists) {
      onCellClick(rowValue, colValue, cell);
    }
  };

  // Common attributes for attribute selection
  const commonAttributes = [
    { value: 'size', label: 'Size' },
    { value: 'color', label: 'Color' },
    { value: 'material', label: 'Material' },
    { value: 'style', label: 'Style' },
  ];

  return (
    <Card
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: theme.radius.md,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GridViewIcon sx={{ color: palette.primary, fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
              }}
            >
              Variant Matrix
            </Typography>
          </Box>

          {matrix && (
            <Chip
              icon={<CheckCircleIcon />}
              label={`${matrix.totalVariants} of ${matrix.totalCombinations} variants`}
              size="small"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                backgroundColor: alpha(palette.primary, 0.1),
                color: palette.primary,
              }}
            />
          )}
        </Box>

        {/* Attribute Selection */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Rows</InputLabel>
            <Select
              value={rowAttribute}
              label="Rows"
              onChange={(e) => setRowAttribute(e.target.value)}
              sx={{
                backgroundColor: colors.surfaceElevated,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border,
                },
              }}
            >
              {commonAttributes.map((attr) => (
                <MenuItem key={attr.value} value={attr.value}>
                  {attr.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Columns</InputLabel>
            <Select
              value={columnAttribute}
              label="Columns"
              onChange={(e) => setColumnAttribute(e.target.value)}
              sx={{
                backgroundColor: colors.surfaceElevated,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border,
                },
              }}
            >
              {commonAttributes.map((attr) => (
                <MenuItem key={attr.value} value={attr.value}>
                  {attr.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rectangular" height={60} sx={{ borderRadius: theme.radius.sm }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radius.sm }} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              backgroundColor: alpha(palette.error, 0.1),
              color: palette.error,
              border: `1px solid ${palette.error}`,
            }}
          >
            Failed to load variant matrix. Please try again.
          </Alert>
        )}

        {/* Matrix Grid */}
        {!isLoading && !error && matrix && (
          <>
            {matrix.rowValues.length === 0 || matrix.columnValues.length === 0 ? (
              <Alert
                severity="info"
                sx={{
                  backgroundColor: alpha(palette.primary, 0.05),
                  color: colors.textPrimary,
                  border: `1px solid ${palette.primary}`,
                }}
              >
                No variants found. Create variants by selecting combinations below or use the bulk creation dialog.
              </Alert>
            ) : (
              <TableContainer
                sx={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: theme.radius.sm,
                  backgroundColor: colors.surfaceElevated,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: alpha(palette.primary, 0.05),
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.wider,
                          color: colors.textMuted,
                          borderBottom: `2px solid ${colors.border}`,
                          py: 2,
                        }}
                      >
                        {matrix.rowAttribute} / {matrix.columnAttribute}
                      </TableCell>
                      {matrix.columnValues.map((colValue) => (
                        <TableCell
                          key={colValue}
                          align="center"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.xs,
                            fontWeight: typography.fontWeight.bold,
                            textTransform: 'uppercase',
                            letterSpacing: typography.letterSpacing.wider,
                            color: colors.textPrimary,
                            borderBottom: `2px solid ${colors.border}`,
                            py: 2,
                          }}
                        >
                          {colValue}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matrix.rowValues.map((rowValue, rowIndex) => (
                      <TableRow
                        key={rowValue}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(palette.primary, 0.02),
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.semibold,
                            color: colors.textPrimary,
                            borderRight: `1px solid ${colors.border}`,
                            py: 2,
                          }}
                        >
                          {rowValue}
                        </TableCell>
                        {matrix.columnValues.map((colValue, colIndex) => {
                          const cell = matrix.cells[rowIndex]?.[colIndex] || { exists: false };

                          return (
                            <TableCell
                              key={`${rowValue}-${colValue}`}
                              align="center"
                              sx={{
                                py: 2,
                                cursor: cell.exists ? 'pointer' : 'default',
                                backgroundColor: cell.exists
                                  ? alpha(palette.success, 0.05)
                                  : 'transparent',
                                '&:hover': cell.exists ? {
                                  backgroundColor: alpha(palette.success, 0.1),
                                } : {},
                              }}
                              onClick={() => handleCellClickInternal(rowValue, colValue, cell)}
                            >
                              {cell.exists ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                  <Checkbox
                                    checked={cell.isActive}
                                    size="small"
                                    sx={{
                                      color: palette.success,
                                      '&.Mui-checked': {
                                        color: palette.success,
                                      },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCellToggle(rowValue, colValue, cell);
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: typography.fontFamily.mono,
                                      fontSize: typography.fontSize.xs,
                                      color: colors.textMuted,
                                    }}
                                  >
                                    {cell.sku}
                                  </Typography>
                                  {cell.priceAdjustment !== 0 && cell.priceAdjustment !== undefined && (
                                    <Chip
                                      label={cell.priceAdjustment > 0 ? `+$${cell.priceAdjustment}` : `-$${Math.abs(cell.priceAdjustment)}`}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: typography.fontSize.xs,
                                        fontFamily: typography.fontFamily.mono,
                                        backgroundColor: cell.priceAdjustment > 0
                                          ? alpha(palette.success, 0.1)
                                          : alpha(palette.warning, 0.1),
                                        color: cell.priceAdjustment > 0
                                          ? palette.success
                                          : palette.warning,
                                      }}
                                    />
                                  )}
                                  {cell.totalStock !== undefined && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: typography.fontFamily.mono,
                                        fontSize: typography.fontSize.xs,
                                        color: cell.totalStock > 0 ? palette.success : palette.error,
                                      }}
                                    >
                                      Stock: {cell.totalStock}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Checkbox
                                    checked={false}
                                    size="small"
                                    sx={{
                                      color: colors.borderLight,
                                      '&:hover': {
                                        backgroundColor: alpha(palette.primary, 0.05),
                                      },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCellToggle(rowValue, colValue, cell);
                                    }}
                                  />
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Summary */}
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: theme.radius.sm,
                backgroundColor: alpha(palette.primary, 0.05),
                border: `1px solid ${colors.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  color: colors.textMuted,
                  display: 'block',
                }}
              >
                Base Price: ${product.price.toFixed(2)} |
                Total Possible Combinations: {matrix.totalCombinations} |
                Active Variants: {matrix.totalVariants}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};
